import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "./i18n/locales";

const en = {
  meta: {
    title: "Drop Chance Calculator",
  },
  app: {
    titleLine1: "Drop Chance",
    titleLine2: "Calculator",
  },
  share: {
    share: "Share",
    copy: "Copy",
    copied: "Copied",
  },
  labels: {
    oneIn: "1 in",
    runs: "runs",
    percent: "%",
    luckMeter: "Luck Meter",
  },
  headlines: {
    chanceAtLeastOnce: "Chance for the item to drop at least once",
    expectedRunsAtLeastOnce: "Expected runs for the item to drop at least once",
    myDropChances: "My drop chances",
  },
  table: {
    item: "Item",
    chance: "Chance",
    runs: "Runs",
    likelihood: "Likelihood",
  },
  emptyList: {
    textBefore: 'Click "',
    addItem: "Add item",
    plus: "+",
    textAfter: '" to track the drop chances of a specific item.',
  },
  actions: {
    addItem: "Add item",
    downloadCsv: "Download CSV",
  },
  dialog: {
    removeTitle: "Remove item",
    confirmRemove: 'Confirm to remove "{{title}}"',
    confirmRemoveEmpty: "Confirm to remove empty item",
    cancel: "Cancel",
    remove: "Remove",
  },
  csv: {
    item: "Item",
    dropchance: "Drop Chance",
    runs: "Runs",
    likelihood: "Likelihood",
    found: "Found",
    yes: "YES",
  },
  footer: {
    createdBy: "Created by",
    from: "from",
    fromAppendix: "",
    howDropChancesWork: "How Drop Chances Work",
    imprint: "Imprint",
    privacy: "Privacy",
    language: "Language",
  },
};

const es = {
  meta: {
    title: "Calculadora de Probabilidad de Drop",
  },
  app: {
    titleLine1: "Probabilidad",
    titleLine2: "de Drop",
  },
  share: {
    share: "Compartir",
    copy: "Copiar",
    copied: "Copiado",
  },
  labels: {
    oneIn: "1 de",
    runs: "runs",
    percent: "%",
    luckMeter: "Suerte",
  },
  headlines: {
    chanceAtLeastOnce: "Probabilidad de que el ítem caiga al menos una vez",
    expectedRunsAtLeastOnce:
      "Intentos esperados para que el ítem caiga al menos una vez",
    myDropChances: "Mis probabilidades de drop",
  },
  table: {
    item: "Ítem",
    chance: "Probabilidad",
    runs: "Runs",
    likelihood: "Resultado",
  },
  emptyList: {
    textBefore: 'Haz clic en "',
    addItem: "Añadir ítem",
    plus: "+",
    textAfter:
      '" para seguir las probabilidades de drop de un ítem específico.',
  },
  actions: {
    addItem: "Añadir ítem",
    downloadCsv: "Descargar CSV",
  },
  dialog: {
    removeTitle: "Quitar ítem",
    confirmRemove: 'Confirma para quitar "{{title}}"',
    confirmRemoveEmpty: "Confirma para quitar un ítem vacío",
    cancel: "Cancelar",
    remove: "Quitar",
  },
  csv: {
    item: "Ítem",
    dropchance: "Prob. de drop",
    runs: "Intentos",
    likelihood: "Resultado",
    found: "Conseguido",
    yes: "SÍ",
  },
  footer: {
    createdBy: "Creado por",
    from: "de",
    fromAppendix: "",
    howDropChancesWork: "Cómo funcionan las probabilidades de drop",
    imprint: "Aviso legal",
    privacy: "Privacidad",
    language: "Idioma",
  },
};

const ja = {
  meta: {
    title: "ドロップ率計算機",
  },
  app: {
    titleLine1: "ドロップ確率",
    titleLine2: "計算機",
  },
  share: {
    share: "共有",
    copy: "コピー",
    copied: "完了",
  },
  labels: {
    oneIn: "1/",
    runs: "回",
    percent: "%",
    luckMeter: "運ゲージ",
  },
  headlines: {
    chanceAtLeastOnce: "アイテムが少なくとも1回ドロップする確率",
    expectedRunsAtLeastOnce: "アイテムが少なくとも1回ドロップするまでの期待試行回数",
    myDropChances: "ドロップ率",
  },
  table: {
    item: "アイテム",
    chance: "確率",
    runs: "回数",
    likelihood: "ドロップ率",
  },
  emptyList: {
    textBefore: "「",
    addItem: "アイテムを追加",
    plus: "+",
    textAfter: "」をクリックして、特定のアイテムのドロップ率を追跡します。",
  },
  actions: {
    addItem: "アイテムを追加",
    downloadCsv: "CSVをダウンロード",
  },
  dialog: {
    removeTitle: "アイテムを削除",
    confirmRemove: "「{{title}}」を削除しますか？",
    confirmRemoveEmpty: "空のアイテムを削除しますか？",
    cancel: "キャンセル",
    remove: "削除",
  },
  csv: {
    item: "アイテム",
    dropchance: "ドロップ率",
    runs: "回数",
    likelihood: "ドロップ率",
    found: "獲得済み",
    yes: "はい",
  },
  footer: {
    createdBy: "作成者",
    from: "·",
    fromAppendix: "制作",
    howDropChancesWork: "ドロップ確率の仕組み",
    imprint: "インプリント",
    privacy: "プライバシー",
    language: "言語",
  },
};

const ko = {
  meta: {
    title: "드롭률 계산기",
  },
  app: {
    titleLine1: "드롭률",
    titleLine2: "계산기",
  },
  share: {
    share: "공유",
    copy: "복사",
    copied: "복사됨",
  },
  labels: {
    oneIn: "1/",
    runs: "회",
    percent: "%",
    luckMeter: "행운 게이지",
  },
  headlines: {
    chanceAtLeastOnce: "아이템이 최소 한 번 드롭될 확률",
    expectedRunsAtLeastOnce: "아이템이 최소 한 번 드롭될 때까지 예상 시도 횟수",
    myDropChances: "내 드롭률",
  },
  table: {
    item: "아이템",
    chance: "확률",
    runs: "횟수",
    likelihood: "드롭률",
  },
  emptyList: {
    textBefore: '"',
    addItem: "아이템 추가",
    plus: "+",
    textAfter: '"를 클릭하여 특정 아이템의 드롭률을 추적하세요.',
  },
  actions: {
    addItem: "아이템 추가",
    downloadCsv: "CSV 다운로드",
  },
  dialog: {
    removeTitle: "아이템 삭제",
    confirmRemove: '"{{title}}"을(를) 삭제하시겠습니까?',
    confirmRemoveEmpty: "빈 아이템을 삭제하시겠습니까?",
    cancel: "취소",
    remove: "삭제",
  },
  csv: {
    item: "아이템",
    dropchance: "드롭률",
    runs: "횟수",
    likelihood: "획득 확률",
    found: "획득함",
    yes: "예",
  },
  footer: {
    createdBy: "제작",
    from: "·",
    fromAppendix: "제작",
    howDropChancesWork: "드롭률 계산 방식",
    imprint: "법적 고지",
    privacy: "개인정보",
    language: "언어",
  },
};

const zh = {
  meta: {
    title: "掉落概率计算器",
  },
  app: {
    titleLine1: "掉落概率",
    titleLine2: "计算器",
  },
  share: {
    share: "分享",
    copy: "复制",
    copied: "已复制",
  },
  labels: {
    oneIn: "1/",
    runs: "次",
    percent: "%",
    luckMeter: "幸运值",
  },
  headlines: {
    chanceAtLeastOnce: "物品至少掉落一次的概率",
    expectedRunsAtLeastOnce: "物品至少掉落一次所需的预期次数",
    myDropChances: "我的掉率",
  },
  table: {
    item: "物品",
    chance: "概率",
    runs: "次数",
    likelihood: "掉落率",
  },
  emptyList: {
    textBefore: "点击「",
    addItem: "添加物品",
    plus: "+",
    textAfter: "」以追踪特定物品的掉落概率。",
  },
  actions: {
    addItem: "添加物品",
    downloadCsv: "下载 CSV",
  },
  dialog: {
    removeTitle: "删除物品",
    confirmRemove: "确认删除「{{title}}」？",
    confirmRemoveEmpty: "确认删除空物品？",
    cancel: "取消",
    remove: "删除",
  },
  csv: {
    item: "物品",
    dropchance: "掉落概率",
    runs: "次数",
    likelihood: "掉落概率",
    found: "已获得",
    yes: "是",
  },
  footer: {
    createdBy: "作者",
    from: "·",
    fromAppendix: "制作",
    howDropChancesWork: "掉落概率原理",
    imprint: "法律声明",
    privacy: "隐私",
    language: "语言",
  },
};

const resources = {
  en: { translation: en },
  es: { translation: es },
  ja: { translation: ja },
  ko: { translation: ko },
  zh: { translation: zh },
};

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: true,
});

export default i18n;
